import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ListMusicIcon, PencilIcon, PlayIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { TrackTable } from '../components/TrackTable';
import { useLibrary } from '../contexts/LibraryContext';
import { usePlayer } from '../contexts/PlayerContext';
type Tab = 'playlists' | 'saved';
export function Library() {
  const {
    favorites,
    playlists,
    createPlaylist,
    renamePlaylist,
    deletePlaylist
  } = useLibrary();
  const {
    play
  } = usePlayer();
  const [tab, setTab] = useState<Tab>('playlists');
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const tabs: Array<{
    id: Tab;
    label: string;
    count: number;
  }> = [{
    id: 'playlists',
    label: 'Playlists',
    count: playlists.length
  }, {
    id: 'saved',
    label: 'Saved tracks',
    count: favorites.length
  }];
  return <div className="px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-285" data-mp="3elre326pinck">
      <h1 className="font-display text-4xl text-cream" data-id="element-286" data-mp="3enatyrogawdt">Your Library</h1>
      <p className="mt-2 text-sm text-muted" data-id="element-287" data-mp="39lv66ruw2brq">Everything here lives in this browser — it survives a refresh.</p>

      <div role="tablist" aria-label="Library sections" className="mt-7 flex gap-1 border-b border-ink-line" data-id="element-288" data-mp="35m4aqauf37gv">
        {tabs.map(({
        id,
        label,
        count
      }) => <button key={id} role="tab" type="button" aria-selected={tab === id} onClick={() => setTab(id)} className={`relative px-4 py-2.5 text-sm transition-colors duration-150 ease-night focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft ${tab === id ? 'text-cream' : 'text-muted hover:text-cream'}`} data-id="element-289" data-mp="16v13ragodz5">
            {label} <span className="tabular-nums text-muted" data-id="element-290" data-mp="kwn9jrxnuv0f">{count}</span>
            {tab === id && <motion.span layoutId="library-tab" transition={{
          duration: 0.24,
          ease: [0.23, 1, 0.32, 1]
        }} className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-gold" data-id="element-291" data-mp="g2ssyxdcqw5k" />}
          </button>)}
      </div>

      <AnimatePresence mode="wait" data-id="element-292" data-mp="q7j0diriysh6">
        {tab === 'playlists' ? <motion.div key="playlists" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-8" data-id="element-293" data-mp="u9e22oddb7vh">
            <form onSubmit={e => {
          e.preventDefault();
          if (!name.trim()) return;
          createPlaylist(name);
          setName('');
        }} className="flex max-w-md gap-2" data-id="element-294" data-mp="79pymjsn0old">
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Name a new set…" aria-label="New playlist name" className="min-w-0 flex-1 rounded-full border border-ink-line bg-ink-panel px-4 py-2.5 text-sm text-cream placeholder:text-muted focus:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-295" data-mp="21ld3kz58u5fh" />
              <button type="submit" className="flex items-center gap-1.5 rounded-full bg-gold px-4 py-2.5 text-sm font-medium text-ink-base transition-transform duration-150 ease-night hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base" data-id="element-296" data-mp="3v557inf3clc7">
                <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-297" data-mp="hr1v1vr7o58h" />
                Create
              </button>
            </form>

            {playlists.length === 0 ? <p className="mt-10 max-w-md text-sm leading-relaxed text-muted" data-id="element-298" data-mp="2jiof1dcoqzvg">
                No sets yet. Name one above, or use the <span className="text-cream" data-id="element-299" data-mp="2lxuwrn37iafw">＋</span> beside any track to
                build one as you listen.
              </p> : <ul className="mt-9 grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-id="element-300" data-mp="2px3rau62mcci">
                {playlists.map((playlist, i) => {
            const covers = playlist.tracks.slice(0, 4);
            const renaming = editingId === playlist.id;
            return <motion.li key={playlist.id} layout initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} whileHover={{
              y: -6,
              rotate: i % 2 === 0 ? -0.8 : 0.8
            }} transition={{
              duration: 0.24,
              ease: [0.23, 1, 0.32, 1],
              delay: Math.min(i, 8) * 0.03
            }} className="group relative flex gap-4 overflow-hidden rounded-lg border border-ink-line bg-ink-panel/70 p-4 transition-colors duration-200 ease-night hover:border-gold/40" data-id="element-301" data-mp="22anjxtpppd3x" data-mpi={i}>
                      <span aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full opacity-0 blur-3xl transition-opacity duration-300 ease-night group-hover:opacity-100" style={{
                background: 'radial-gradient(circle, rgb(var(--accent) / 0.35) 0%, rgb(var(--accent) / 0) 70%)'
              }} data-id="element-302" data-mp="1gxn8te9puytp" />
                      <div className="relative grid h-20 w-20 flex-none grid-cols-2 grid-rows-2 overflow-hidden rounded bg-ink-raised ring-1 ring-cream/5" data-id="element-303" data-mp="t7j576wj2z2p">
                        {covers.length === 0 && <span className="col-span-2 row-span-2 flex items-center justify-center" data-id="element-304" data-mp="j8jttgkdopc0">
                            <ListMusicIcon className="h-6 w-6 text-muted" aria-hidden="true" data-id="element-305" data-mp="edo0md3envxb" />
                          </span>}
                        {covers.map(track => <img key={track.id} src={track.artworkSmall} alt="" loading="lazy" className={`h-full w-full object-cover transition-transform duration-300 ease-night group-hover:scale-105 ${covers.length === 1 ? 'col-span-2 row-span-2' : ''}`} data-id="element-306" data-mp="3vwd5nnehwho4" />)}
                      </div>
                      <div className="relative min-w-0 flex-1" data-id="element-307" data-mp="9nk995ggz9yz">
                        {renaming ? <form onSubmit={e => {
                  e.preventDefault();
                  renamePlaylist(playlist.id, draft);
                  setEditingId(null);
                }} className="flex gap-2" data-id="element-308" data-mp="ellunx1dbldm">
                            <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} aria-label={`Rename ${playlist.name}`} className="min-w-0 flex-1 rounded border border-ink-line bg-ink-base px-2 py-1.5 text-sm text-cream focus:border-gold/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-309" data-mp="1gf5bnxbftwpu" />
                            <button type="submit" aria-label="Save name" className="flex h-8 w-8 flex-none items-center justify-center rounded bg-gold text-ink-base focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-310" data-mp="tj53oxfpq9x3">
                              <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-311" data-mp="2dvu8tdpg0asw" />
                            </button>
                          </form> : <h2 className="truncate font-display text-xl text-cream" data-id="element-312" data-mp="g8a0n1d9l3gk">
                            <Link to={`/playlist/${playlist.id}`} className="rounded transition-colors duration-150 ease-night hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-313" data-mp="l7mkrecr3wfg">
                              {playlist.name}
                            </Link>
                          </h2>}
                        <p className="mt-1 truncate text-xs text-muted" data-id="element-314" data-mp="13z5mlhv1h4fw">
                          {playlist.tracks.length} {playlist.tracks.length === 1 ? 'track' : 'tracks'}
                          {playlist.note ? ` · ${playlist.note}` : ''}
                        </p>
                        <div className="mt-3 flex items-center gap-1.5" data-id="element-315" data-mp="3aoaj0ztqowfm">
                          <button type="button" onClick={() => play(playlist.tracks, 0)} disabled={playlist.tracks.length === 0} aria-label={`Play ${playlist.name}`} className="flex items-center gap-1.5 rounded-full border border-ink-line px-3 py-1.5 text-xs text-cream transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-316" data-mp="335ayurygf0v7">
                            <PlayIcon className="h-3 w-3" aria-hidden="true" data-id="element-317" data-mp="3c5py6v896ghp" />
                            Play
                          </button>
                          <button type="button" onClick={() => {
                    setDraft(playlist.name);
                    setEditingId(renaming ? null : playlist.id);
                  }} aria-label={`Rename ${playlist.name}`} className="rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-318" data-mp="3844bki5x2nub">
                            <PencilIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-319" data-mp="6klkf1rgp99p" />
                          </button>
                          <button type="button" onClick={() => deletePlaylist(playlist.id)} aria-label={`Delete ${playlist.name}`} className="rounded-full p-1.5 text-muted transition-colors duration-150 ease-night hover:text-iris-soft focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-320" data-mp="l8zjr2po6fli">
                            <Trash2Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-321" data-mp="2r3itqw30k8mg" />
                          </button>
                        </div>
                      </div>
                    </motion.li>;
          })}
              </ul>}
          </motion.div> : <motion.div key="saved" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -6
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} className="mt-8" data-id="element-322" data-mp="xterb9alutrh">
            {favorites.length === 0 ? <div className="max-w-md" data-id="element-323" data-mp="sv3c0j1jjr1v">
                <h2 className="font-display text-2xl italic text-gold" data-id="element-324" data-mp="srz53dwoyh7i">Nothing saved yet</h2>
                <p className="mt-3 text-sm leading-relaxed text-muted" data-id="element-325" data-mp="ewdifddxk9i9">
                  Tap the heart beside any track and it lands here.
                </p>
                <Link to="/search" className="mt-6 inline-block rounded-full border border-ink-line px-5 py-2.5 text-sm text-cream transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-326" data-mp="299d4npctg1yi">
                  Go find something
                </Link>
              </div> : <>
                <button type="button" onClick={() => play(favorites, 0)} className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-medium text-ink-base transition-transform duration-150 ease-night hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base" data-id="element-327" data-mp="3d809gnjqod1q">
                  <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-328" data-mp="3r4vlmovaekm0" />
                  Play all
                </button>
                <div className="mt-6 overflow-x-auto scroll-thin" data-id="element-329" data-mp="shhmb54fhtos">
                  <TrackTable tracks={favorites} data-id="element-330" data-mp="noy9iadkf2l1" />
                </div>
              </>}
          </motion.div>}
      </AnimatePresence>
    </div>;
}